import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { settingsApi } from '@/lib/api/settings.api';

import type { UpdateSettingDto, BulkUpdateSettingsDto } from '@repo/shared-types';

export function useSettings(category?: string) {
  return useQuery({
    queryKey: ['settings', category],
    queryFn: () => settingsApi.getSettings(category),
  });
}

export function useEncryptionStatus() {
  return useQuery({
    queryKey: ['settings', 'encryption-status'],
    queryFn: () => settingsApi.getEncryptionStatus(),
  });
}

export function useSetting(key: string) {
  return useQuery({
    queryKey: ['settings', 'single', key],
    queryFn: () => settingsApi.getSetting(key),
    enabled: !!key,
    // Settings rarely change and this hook mounts per chat card — without a
    // staleTime every card mount refires the same request.
    staleTime: 60_000,
  });
}

export function useUpdateSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, data }: { key: string; data: UpdateSettingDto }) =>
      settingsApi.updateSetting(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

export function useBulkUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkUpdateSettingsDto) => settingsApi.bulkUpdateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}
